import React, { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDownIcon, CircleDashedIcon, XIcon } from 'lucide-react';
import type { ValidationStatus } from '../../types/dashboard';
import { metricDefinitions } from '../../data/definitions';
import { useDefinitions } from '../../contexts/DefinitionsContext';
import { SearchInput } from '../ui/TableControls';
import { ConversionWindowExample, MultiTerritoryExample } from '../funnel/CountingExamples';
import { DedupExample } from '../retention/DedupExample';
const STATUS_STYLE: Record<ValidationStatus, string> = {
  Confirmed: 'bg-positive-soft text-positive',
  'Confirmed rule · build pending': 'border border-positive/30 bg-white text-positive',
  Proposed: 'bg-navy-50 text-navy',
  'Pending validation': 'border border-dashed border-caution/40 bg-caution-soft text-caution',
  Illustrative: 'border border-dashed border-line-strong bg-canvas text-ink-muted',
  'Not in scope': 'bg-canvas text-ink-subtle'
};
const DASHED: ValidationStatus[] = ['Pending validation', 'Illustrative'];
const EXAMPLES: Record<string, () => React.ReactNode> = {
  members: () => <MultiTerritoryExample data-id="element-76" data-mp="3tknx5v2vx5ui" />,
  conversion: () => <ConversionWindowExample data-id="element-77" data-mp="2djqx2ekdnn5" />,
  regsPerMember: () => <ConversionWindowExample data-id="element-78" data-mp="2ap6havjw2tq" />,
  seasonRetention: () => <DedupExample data-id="element-79" data-mp="7ajxdefbsses" />,
  yoyRetention: () => <DedupExample data-id="element-80" data-mp="79iieyv6vlrs" />
};
export function DefinitionsDrawer() {
  const {
    isOpen,
    openId,
    close
  } = useDefinitions();
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);
  useEffect(() => {
    if (!isOpen) return;
    setQuery('');
    setExpanded(openId ?? metricDefinitions[0].id);
    const t = setTimeout(() => {
      if (openId) document.getElementById(`def-${openId}`)?.scrollIntoView({
        block: 'start'
      });
    }, 60);
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close();
    document.addEventListener('keydown', onKey);
    return () => {
      clearTimeout(t);
      document.removeEventListener('keydown', onKey);
    };
  }, [isOpen, openId, close]);
  const items = useMemo(() => {
    const q = query.trim().toLowerCase();
    return metricDefinitions.filter(d => !q || [d.name, d.definition, d.calculation, d.identifier, d.status, d.statusDetail, d.note ?? '', ...(d.openItems ?? [])].some(s => s.toLowerCase().includes(q)));
  }, [query]);
  return <AnimatePresence data-id="element-81" data-mp="213bhbyjxij8w">
      {isOpen && <div key="definitions" className="fixed inset-0 z-[60]" data-id="element-82" data-mp="2143g1i8tox3e">
          <motion.div className="absolute inset-0 bg-ink/30" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} onClick={close} data-id="element-83" data-mp="1x1zzwgb7l4bs" />
          <motion.aside role="dialog" aria-modal="true" aria-label="Metric definitions" initial={{
        x: 32,
        opacity: 0
      }} animate={{
        x: 0,
        opacity: 1
      }} exit={{
        x: 32,
        opacity: 0
      }} transition={{
        duration: 0.24,
        ease: [0.23, 1, 0.32, 1]
      }} className="absolute right-0 top-0 flex h-full w-full max-w-[560px] flex-col bg-white shadow-2xl" data-id="element-84" data-mp="260bpqg493y36">
            <header className="border-b border-line px-5 py-4" data-id="element-85" data-mp="185r8zn4ywvc2">
              <div className="flex items-start justify-between gap-3" data-id="element-86" data-mp="u9e217y28cbu">
                <div data-id="element-87" data-mp="z6npii1w2dx1">
                  <h2 className="text-lg font-semibold text-navy" data-id="element-88" data-mp="z8784fb0p11z">Metric definitions</h2>
                  <p className="text-xs text-ink-muted" data-id="element-89" data-mp="1mwi2r0q4e4ht">Scoping reference — not an approved reporting specification. Only items marked Confirmed are agreed business rules.</p>
                </div>
                <button type="button" onClick={close} aria-label="Close definitions" className="rounded p-1 text-ink-muted hover:bg-canvas hover:text-ink" data-id="element-90" data-mp="1d7qassl27k6w">
                  <XIcon className="h-4 w-4" data-id="element-91" data-mp="1i3oui2vg0e0z" />
                </button>
              </div>
              <div className="mt-3" data-id="element-92" data-mp="25tj84mnl66ma">
                <SearchInput value={query} onChange={setQuery} placeholder="Search definitions" data-id="element-93" data-mp="2amugwtbykxgu" />
              </div>
            </header>
            <ul className="flex-1 divide-y divide-line overflow-y-auto" data-id="element-94" data-mp="m22v9euqetuc">
              {items.length === 0 && <li className="px-5 py-10 text-center text-xs text-ink-muted" data-id="element-95" data-mp="h0n6bbpfo36o">No definitions match.</li>}
              {items.map(d => {
            const isExpanded = expanded === d.id;
            const Example = EXAMPLES[d.id];
            return <li key={d.id} id={`def-${d.id}`} className={isExpanded ? 'bg-canvas/40' : ''} data-id="element-96" data-mp="14rj0gfhd3ah3">
                    <button type="button" aria-expanded={isExpanded} onClick={() => setExpanded(isExpanded ? null : d.id)} className="flex w-full items-center gap-3 px-5 py-3 text-left hover:bg-canvas" data-id="element-97" data-mp="10ow3y19qx1dk">
                      <span className="flex-1" data-id="element-98" data-mp="1og1rl2lxgxyf">
                        <span className="block text-[14px] font-semibold text-navy" data-id="element-99" data-mp="1ogtdflpq3hbi">{d.name}</span>
                        {d.supporting && <span className="block text-[11px] text-ink-subtle" data-id="element-100" data-mp="1qzeoe87o7wwb">Supporting definition</span>}
                      </span>
                      <span className={`inline-flex items-center gap-1 whitespace-nowrap rounded px-1.5 py-0.5 text-[11px] font-semibold ${STATUS_STYLE[d.status]}`} data-id="element-101" data-mp="1vzkhlstl9rgi">
                        {DASHED.includes(d.status) && <CircleDashedIcon className="h-3 w-3" aria-hidden data-id="element-102" data-mp="2508vmnizfrte" />}
                        {d.status}
                      </span>
                      <ChevronDownIcon className={`h-4 w-4 text-ink-subtle transition-transform duration-150 ${isExpanded ? 'rotate-180' : ''}`} data-id="element-103" data-mp="29w6diy9w968a" />
                    </button>
                    {isExpanded && <div className="space-y-3 px-5 pb-5" data-id="element-104" data-mp="2jqg6oi6nu8k2">
                        <p className="text-[13px] leading-relaxed text-ink" data-id="element-105" data-mp="2olvjr7wug0ff">{d.definition}</p>
                        <div className="rounded-md border border-line bg-white px-3 py-2" data-id="element-106" data-mp="2sq364yftx4ah">
                          <div className="text-[11px] font-semibold text-ink-muted" data-id="element-107" data-mp="2xowlkweof5da">Proposed calculation</div>
                          <p className="mt-0.5 font-mono text-[12px] leading-relaxed text-ink" data-id="element-108" data-mp="xmev76wetfee">{d.calculation}</p>
                        </div>
                        <dl className="grid grid-cols-[120px_1fr] gap-x-3 gap-y-2 text-xs" data-id="element-109" data-mp="17bey31e18uxv">
                          <dt className="text-ink-muted" data-id="element-110" data-mp="17d9not091b8a">Counting identifier</dt>
                          <dd className="font-medium text-ink" data-id="element-111" data-mp="12jdnrl0fdeah">{d.identifier}</dd>
                          <dt className="text-ink-muted" data-id="element-112" data-mp="1h81i5nyjbhyu">Counting grain</dt>
                          <dd className="font-medium text-ink" data-id="element-113" data-mp="1cbb6rdxodajr">{d.grain}</dd>
                          <dt className="text-ink-muted" data-id="element-114" data-mp="1qa0zyihdiq2u">Date basis</dt>
                          <dd className="font-medium text-ink" data-id="element-115" data-mp="1q86n7rg9a49u">{d.dateBasis}</dd>
                          <dt className="text-ink-muted" data-id="element-116" data-mp="1lbzi20voowsy">Validation status</dt>
                          <dd className="font-medium text-ink" data-id="element-117" data-mp="3s2fgbd49rmh5">{d.statusDetail}</dd>
                          {d.filters.length > 0 && <>
                              <dt className="text-ink-muted" data-id="element-118" data-mp="22k0gnuhv91p8">Applies filters</dt>
                              <dd className="flex flex-wrap gap-1" data-id="element-119" data-mp="27jlokuf0kke3">
                                {d.filters.map(f => <span key={f} className="rounded border border-line bg-white px-1.5 py-0.5 text-[11px] text-ink-muted" data-id="element-120" data-mp="1tt04519ww2e3">{f}</span>)}
                              </dd>
                            </>}
                        </dl>
                        {d.note && <p className="rounded-md bg-caution-soft px-3 py-2 text-xs text-caution" data-id="element-121" data-mp="1ouf5v7e7zssa">
                            <b data-id="element-122" data-mp="1av5nc12jkml5">Prototype assumption:</b> {d.note}
                          </p>}
                        {d.openItems && d.openItems.length > 0 && <div className="rounded-md border border-dashed border-caution/40 bg-white px-3 py-2" data-id="element-123" data-mp="357vhmjwtnxt2">
                            <div className="text-[11px] font-semibold text-caution" data-id="element-124" data-mp="396uvwv4g16x6">Still to confirm</div>
                            <ul className="mt-1 list-disc space-y-0.5 pl-4 text-xs text-ink" data-id="element-125" data-mp="1x1oqn7dxpiwt">
                              {d.openItems.map(o => <li key={o} data-id="element-126" data-mp="1n8r14haiytop">{o}</li>)}
                            </ul>
                          </div>}
                        {Example && <div className="rounded-md border border-line bg-white p-3.5" data-id="element-127" data-mp="1e9vsyznx79ir">
                            <div className="mb-2.5 text-[11px] font-semibold text-ink-muted" data-id="element-128" data-mp="198zp2m3kjndc">Worked example (sample)</div>
                            {Example()}
                          </div>}
                      </div>}
                  </li>;
          })}
            </ul>
          </motion.aside>
        </div>}
    </AnimatePresence>;
}